import { Box, Button, Paper, TextField, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface IProps {
  maxLength?: number;
  onCancel: () => void;
  defaultValue?: string;
  onValueSubmit: (value: string) => void;
  placeholder?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  wrap: {
    boxSizing: 'border-box',
    zIndex: 1,
    position: 'fixed',
    width: 470,
    height: 300,
    right: 24,
    bottom: 24,
    padding: 30,
    boxShadow: '0 24px 24px 0 #0000004c, 0 0 24px 0 #00000038',
    backgroundImage: `linear-gradient(to bottom, ${theme.palette.background.paper.toString()}, ${theme.palette.background.paper.toString()}), linear-gradient(to bottom, ${theme.palette.background.paper.toString()}cc, ${theme.palette.background.paper.toString()}66 5%, ${theme.palette.background.paper.toString()}00 20%, ${theme.palette.background.paper.toString()}00)`,
    borderWidth: 0.5,
  },
  length: {
    color: theme.palette.text.primary,
    opacity: 0.54,
    fontSize: 12,
    fontWeight: 500,
  },
  inputWrap: {
    marginTop: 14,
    width: '100%',
    height: 130,
    overflow: 'auto',
  },
  input: {
    fontSize: 14,
    width: '100%',
    lineHeight: 1.1,
    color: '#a2a2a2',
  },
  buttonWrap: {
    paddingTop: 24,
    marginRight: -4,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    marginRight: theme.spacing(1),
  },
}));

export interface IFormTextFieldInputPopupImperativeHandle {
  focus: () => void;
}

const InputPopup = (props: IProps, ref: Ref<IFormTextFieldInputPopupImperativeHandle | undefined>): JSX.Element => {
  const classes = useStyles();

  const { onValueSubmit, onCancel, maxLength = 2000, defaultValue = '', placeholder = '' } = props;
  const [text, setText] = useState<string>(defaultValue);

  const popupRef = useRef<HTMLInputElement>();

  useImperativeHandle<unknown, IFormTextFieldInputPopupImperativeHandle>(
    ref,
    () => ({
      focus: () => popupRef.current?.focus(),
    }),
    [],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = e.target.value as string;

    if (text !== undefined) {
      setText(text.substr(0, maxLength));
    }
  };

  const handleSaveClick = () => {
    onValueSubmit(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  useEffect(() => {
    popupRef.current?.focus();
  }, []);

  return (
    <Paper className={classes.wrap}>
      <Typography variant="h6">Writer&#39;s Biography</Typography>
      <Typography className={classes.length}>{`${text.length} / ${maxLength}`}</Typography>
      <Box className={classes.inputWrap}>
        <TextField
          multiline
          value={text}
          onChange={handleChange}
          placeholder={placeholder}
          InputProps={{
            disableUnderline: true,
          }}
          onKeyDown={handleKeyDown}
          className={classes.input}
          inputRef={popupRef}
        />
      </Box>
      <Box className={classes.buttonWrap}>
        <Button variant="text" color="default" className={classes.cancelButton} onClick={onCancel}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          style={{
            textTransform: 'uppercase',
          }}
        >
          SAVE
        </Button>
      </Box>
    </Paper>
  );
};

export default forwardRef(InputPopup);
